import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MessageSquarePlus, X } from 'lucide-react';
import { compliance, type QueryCategory, type QueryVisibilityType } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { CAN_RAISE_QUERY_ANY } from '@/lib/permissions';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { errorText } from '@/lib/permissionError';

const EXTERNAL_RAISER_ROLES = new Set(['monitor', 'auditor']);

const CATEGORY_LABELS: Record<QueryCategory, string> = {
  missing_document: 'Missing document',
  incorrect_metadata: 'Incorrect metadata',
  expired_document: 'Expired document',
  compliance_flag: 'Compliance flag',
  other: 'Other',
};

export function RaiseFieldQueryButton({
  fieldId,
  fieldRef,
  formLabel,
  studyId,
  subjectId,
  visitId,
}: {
  fieldId: string;
  fieldRef: string;
  /** The eSource form this field belongs to — carried through as the
   *  query's section_ref so the Queries table shows the form name
   *  instead of a blank Section. */
  formLabel: string;
  studyId: string;
  subjectId: string;
  visitId: string;
}) {
  const { user } = useAuth();
  const perms = useMyPermissions();
  const [open, setOpen] = useState(false);

  // RBAC grid decides (queries.create, 23 Oct 2026).
  if (!user || !perms.holdsOperation('queries.create', CAN_RAISE_QUERY_ANY)) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
        className="hidden shrink-0 rounded p-0.5 text-accent-700 hover:bg-accent-50 group-hover:inline-flex"
        title="Raise a query on this field"
        aria-label="Raise query on field"
      >
        <MessageSquarePlus className="h-3 w-3" />
      </button>
      {open && (
        <RaiseFieldQueryDialog
          fieldId={fieldId}
          fieldRef={fieldRef}
          formLabel={formLabel}
          studyId={studyId}
          subjectId={subjectId}
          visitId={visitId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function RaiseFieldQueryDialog({
  fieldId,
  fieldRef,
  formLabel,
  studyId,
  subjectId,
  visitId,
  onClose,
}: {
  fieldId: string;
  fieldRef: string;
  formLabel: string;
  studyId: string;
  subjectId: string;
  visitId: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [category, setCategory] = useState<QueryCategory>('other');
  const [body, setBody] = useState('');
  // Defaults to how the server would derive it for this caller's role
  // (deriveOrigin), but is an explicit, overridable choice here rather
  // than a silent guess.
  const [visibility, setVisibility] = useState<QueryVisibilityType>(
    user && EXTERNAL_RAISER_ROLES.has(user.role) ? 'external' : 'internal',
  );
  const isExternalRaiser = !!user && EXTERNAL_RAISER_ROLES.has(user.role);
  const visibilityMismatch =
    (isExternalRaiser && visibility === 'internal') ||
    (!isExternalRaiser && visibility === 'external');

  const raise = useMutation({
    mutationFn: () =>
      compliance.raiseQuery({
        study_id: studyId,
        subject_id: subjectId,
        visit_id: visitId,
        field_id: fieldId,
        field_ref: fieldRef,
        section_ref: formLabel,
        category,
        body: body.trim(),
        origin: visibility === 'external' ? 'cra_query' : 'internal',
      }),
    onSuccess: (r) => {
      toast.success(`Query ${r.query_id.slice(0, 8)}… raised on this field.`);
      qc.invalidateQueries({ queryKey: ['compliance-queries'] });
      onClose();
    },
    onError: (err) => toast.error(errorText(err, 'Failed to raise query')),
  });

  return (
    <div
      className="fixed inset-0 z-[105] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="raise-field-query-title"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <div className="min-w-0">
            <h3 id="raise-field-query-title" className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
              <MessageSquarePlus className="h-4 w-4 text-accent-700" />
              Raise a query
            </h3>
            <p className="mt-0.5 truncate text-[11px] text-ink-muted">Field · {fieldRef}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-ink-muted hover:bg-surface-muted/50" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 px-4 py-3">
          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as QueryCategory)}
              className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
            >
              {(Object.keys(CATEGORY_LABELS) as QueryCategory[]).map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </label>

          <fieldset className="text-xs">
            <legend className="font-medium text-ink-secondary">Query type</legend>
            <div className="mt-1 flex gap-4">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="field-query-visibility"
                  checked={visibility === 'internal'}
                  onChange={() => setVisibility('internal')}
                />
                Internal
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio"
                  name="field-query-visibility"
                  checked={visibility === 'external'}
                  onChange={() => setVisibility('external')}
                />
                External
              </label>
            </div>
            {visibilityMismatch && (
              <p className="mt-1.5 rounded border border-danger-200 bg-danger-50 px-2 py-1.5 text-[11px] text-danger-800">
                {isExternalRaiser
                  ? 'Monitors/auditors can only raise External queries.'
                  : 'Site staff can only raise Internal queries.'}
              </p>
            )}
          </fieldset>

          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Description</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Describe the issue with this field…"
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
          </label>
        </div>

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button type="button" onClick={onClose} className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => raise.mutate()}
            disabled={!body.trim() || raise.isPending || visibilityMismatch}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {raise.isPending ? 'Raising…' : 'Raise query'}
          </button>
        </footer>
      </div>
    </div>
  );
}
